import React, { useEffect, useState } from 'react';
import { ArrowRight, ChevronRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { Button } from '../ui/button';
import Slider from 'react-slick';
import { MobileCategoryAccordion } from './MobileCategories';
import { useCustomApi } from '@/contexts/CustomApiContext';
import { useLanguage } from '@/contexts/LanguageContext';
import Link from 'next/link';
import LocalizedClientLink from '@/utils/LocalizedClientLink';


function useWindowWidth() {
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const handleResize = () => setWidth(window.innerWidth);
    handleResize(); // Set initially
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return width;
}

const CategoryHeader = ({ title, description, className, isRTL, handle }) => {
  const { state } = useCustomApi();
  const [currentIndex, setCurrentIndex] = useState(0);



  const categories = state?.customNewCategories?.product_categories || [];
  const slidesPerPage = 5;

  const settings = {
    className: "center",
    dots: false,
    infinite: false,
    arrows: false,
    centerMode: false,
    speed: 500,
    slidesToShow: slidesPerPage,
    swipeToSlide: true,
    slidesToScroll: 2,
    afterChange: (index) => setCurrentIndex(index),
    responsive: [
      { breakpoint: 1440, settings: { arrows: false, slidesToShow: 4 } },
      { breakpoint: 1024, settings: { slidesToShow: 3, slidesToScroll: 1 } },
      {
        breakpoint: 768, settings: {
          slidesToShow: 3,
          slidesToScroll: 1,
          rows: 2,
          slidesPerRow: 1
        }
      }
    ],
  }
  return (
    <div className={`bg-white border border-[#e5e5e5] md:border-none  md:bg-[#F6F6F7] rounded-[16px] p-6 w-full lg:w-[210px] lg:h-full 3xl:w-[250px] 3xl:h-auto flex flex-col  ${className} `}>
      <div className="space-y-2">
        <h3 className="text-[18px] 3xl:text-[22px]  font-semibold font-primary tracking-wider text-[#262626] line-clamp-2">{title}</h3>
        <p className="text-xs 3xl:text-base text-[#262626] leading-relaxed  font-normal font-primary text-justify">{description}</p>
      </div>
      <div className=" block md:hidden flex-grow overflow-hidden">
        <div className="w-full relative mt-5 ml-5">
          <Slider {...settings}>
            {categories.map((item, index) => (
              <div className="max-w-lg md:w-full md:h-full flex flex-col items-center ">
                <div className="relative w-[75px] h-[75px] md:w-auto md:h-[220px] flex justify-center items-center bg-gray-100 p-2 sm:bg-transparent md:bg-[url('/images/bgVector.png')] md:bg-no-repeat md:bg-[left_50%_center] transition-opacity duration-300">
                  <img
                    src={item.metadata?.image || "/images/placeholder.png"}
                    alt={item.name}
                    className="w-auto md:w-[208px] h-auto md:h-[200px] object-cover transition-transform duration-300 ease-in-out md:hover:-translate-y-5"
                  />
                </div>

                <button  aria-label={item.name} className=" w-2/3 md:w-[80%] md:bg-[#F8F8F8] hover:bg-[#D02C2A] font-primary text-[10px] md:text-[16px] font-semibold text-[#262626] md:px-4 md:py-2 hover:text-white mt-1 md:-mt-10 md:ml-6 leading-none md:leading-normal md:rounded-lg">
                  {item.name}
                </button>
              </div>

            ))}
          </Slider>
        </div>
      </div>
      <div className='flex justify-center md:justify-start'>
        <LocalizedClientLink href={`/shop?categories=${handle}`}>
          <button className="inline-flex items-center text-[12px] 3xl:text-sm font-semibold text-gray-600 hover:text-gray-900 transition-colors mt-4 border border-[#737373] w-fit py-2 px-4 rounded-lg"
          aria-label={"categories"}
          >
           {isRTL ? "استعرض الكل" : "Show all"} <ChevronRight className="ml-1 h-5 w-5" />
          </button>
        </LocalizedClientLink>
      </div>
    </div>
  );
}

const ProductItem = ({ image, title }) => (
  <motion.div
    whileHover={{ y: -5 }}
    transition={{ duration: 0.2 }}
    className="group cursor-pointer"
  >
  </motion.div>
);

const CategorySection = ({ title, description, products, isRTL, handle }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const slidesPerPage = 5;

  const settings = {
    dots: false,
    infinite: false,
    arrows: false,
    speed: 500,
    slidesToShow: slidesPerPage,
    swipeToSlide: true,
    slidesToScroll: 2,
    afterChange: (index) => setCurrentIndex(index),
    responsive: [
      { breakpoint: 1440, settings: { arrows: false, slidesToShow: 4 } },
      { breakpoint: 1024, settings: { slidesToShow: 3, slidesToScroll: 1 } },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 3,
          slidesToScroll: 1,
          rows: 2,
          slidesPerRow: 1,
          centerMode: false,
          centerPadding: '0px'
        }
      }
    ],
    variableWidth: false,
    adaptiveHeight: true,
    centerMode: false,
    centerPadding: "0px"
  };

  const width = useWindowWidth();

  let sliceCount = 5;
  if (width < 768) {
    sliceCount = 2;
  } else if (width < 1200) {
    sliceCount = 2;
  } else if (width < 1440) {
    sliceCount = 3;
  }
  else if (width < 1680) {
    sliceCount = 4;
  }else {
    sliceCount = 5;
  }

  return (
    <div className="flex flex-col md:flex-col lg:flex-row gap-6 w-full">
      <div className="flex-shrink-0 max-h-[300px] 3xl:max-h-auto">
        <CategoryHeader title={title} description={description} isRTL={isRTL} handle={handle} />
      </div>
      <div className=" hidden md:block flex-grow overflow-hidden">
        <div className="w-full relative mt-5">
          <div className='flex gap-6'>
          <div className="flex flex-wrap  gap-6">
  {products.map((item, index) => (
    <LocalizedClientLink
      key={index}
      href={`/shop?categories=${item.handle}`}
      className="flex flex-col items-center w-[75px] md:w-auto px-2 group"
    >
      <div className="relative w-[75px] h-[75px] md:w-[220px] md:h-[220px] flex justify-center items-center bg-gray-100 rounded-full sm:bg-transparent md:bg-[url('/images/bgVector.png')] md:bg-no-repeat md:bg-[center] transition-opacity duration-300">
        <img
          src={item.metadata?.image || "/images/placeholder.png"}
          alt={item.name}
          className="w-auto md:w-[208px] h-auto  object-cover transition-transform duration-300 ease-in-out group-hover:-translate-y-3"
        />
      </div>
      <button
        className="pointer-events-none w-full bg-[#F8F8F8] group-hover:bg-[#D02C2A] font-primary text-[10px] md:text-[16px] font-semibold text-[#262626] px-2 md:px-4 py-1 md:py-2 group-hover:text-white mt-3 md:mt-5 leading-none md:leading-normal rounded-lg"
        aria-label={item.name}
      >
        {isRTL ? item.metadata?.name_ar : item.name}
      </button>
    </LocalizedClientLink>
  ))}
</div>


          </div>
        </div>
      </div>
    </div>
  );
};

const AllCategories = () => {
  const { state } = useCustomApi();
  const { language } = useLanguage();
  const isRTL = language === "ar";
  const categories = state?.categories || [];
  console.log(categories, "categories");
  // Get top-level categories (those without a parent_category_id)
  const topLevelCategories = categories.filter(category => category?.category?.parent_category_id === null);


  // Create a map of parent categories with their immediate children
  const groupedCategories = topLevelCategories.reduce((acc, category) => {
    // Find immediate children of this category
    const immediateChildren = category.category.category_children.filter(
      child => child.parent_category_id === category.category.id
    );

    acc[category.category.name] = {
      title: isRTL ? category.category.metadata.name_ar : category.category.name,
      description: isRTL 
          ? (category.category.metadata.description_ar || category.category.description || '')
          : (category.category.description || category.category.metadata.description_ar || ''),
      products: immediateChildren,
      handle: category.category.handle
    };

    return acc;
  }, {});

  console.log(groupedCategories, "groupedCategories");


  return (
    <>
      <MobileCategoryAccordion isRTL={isRTL} />
      <div className="hidden md:block container w-full max-w-full py-2 space-y-12 mb-20">
        <h2 className="text-[20px] md:text-[20px] 2xl:text-[36px] font-semibold tracking-wider font-primary"> {isRTL ? "جميع الفئات" : "All Categories"}</h2>
        <div className="space-y-8 2xl:space-y-20">
          {Object.values(groupedCategories).map((category) => (
            console.log(category, "category"),
            <CategorySection key={category.title} {...category} isRTL={isRTL}/>
          ))}
        </div>
      </div>
    </>
  );
};

export default AllCategories;